import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import Ravelin from 'ravelinjs/core+track+encrypt+promise';
import {
  CHECKOUT_SESSION_STORAGE_RAVELIN_DEVICE_FF,
  CHECKOUT_SESSION_STORAGE_RAVELIN_DEVICE_ID,
  PaymentMethodOptions,
} from '~app/modules/checkout/constants';
import { AdditionalInformation } from '~app/modules/checkout/models/payment-additional-info.model';
import { environment } from '~env/environment';
import { FeatureToggleEnum } from '~shared/constants';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import * as Sentry from '@sentry/browser';
import { ActivatedRoute } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class RavelinService {
  static readonly ROUTER_ID = 'rid';
  static readonly ravelinIdRedirectMap: Partial<{ [key in PaymentMethodOptions]: boolean }> = {
    [PaymentMethodOptions.IDeal]: true,
  }
  private ravelin;
  private ravelinDeviceId;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private featureFlagService: ActiveFeaturesConfigcatService,
    private route: ActivatedRoute
  ) {}

  public init() {
    if (isPlatformBrowser(this.platformId) && RavelinService.getFeatureFlagSession() && !this.ravelinDeviceId) {
      this.ravelinDeviceId = RavelinService.getFromSession();
      return;
    }
    this.featureFlagService
      .isFeatureEnabled(FeatureToggleEnum.FF_ENABLE_RAVELIN_DEVICE_ID)
      .subscribe(async (isRavelinEnabled) => {
        if (isPlatformBrowser(this.platformId) && isRavelinEnabled) {
          try {
            this.ravelin = new Ravelin({ key: environment.ravelinPublishableKey() });
            this.ravelin.core.id().then((deviceId) => {
              this.ravelinDeviceId = deviceId;
              this.updateSessionStorage();
            }).catch(e => this.handleError(e))
          } catch (e) {
            this.ravelin = null;
            this.ravelinDeviceId = null;
            this.handleError(e);
          }
        }
      });
  }

  private handleError(e) {
    Sentry.captureException(e, {
      contexts: {
        info: {
          message: "Payment - FE - failed to init Ravelin"
        }
      }
    });
  }

  get ravelinInstance() {
    return this.ravelin;
  }

  getRavelinDeviceId(): string {
    return this.ravelinDeviceId ?? RavelinService.getFromSession();
  }

  getRavelinObject(): Partial<Record<keyof Pick<AdditionalInformation, 'ravelinDeviceId'>, string>> {
    if (!this.getRavelinDeviceId()) {
      return {};
    }
    return { ravelinDeviceId: this.ravelinDeviceId };
  }

  private updateSessionStorage(): void {
    sessionStorage.setItem(CHECKOUT_SESSION_STORAGE_RAVELIN_DEVICE_ID, this.ravelinDeviceId);
    sessionStorage.setItem(CHECKOUT_SESSION_STORAGE_RAVELIN_DEVICE_FF, 'true');
  }

  static removeFromSession(): void {
    sessionStorage.removeItem(CHECKOUT_SESSION_STORAGE_RAVELIN_DEVICE_ID);
    sessionStorage.removeItem(CHECKOUT_SESSION_STORAGE_RAVELIN_DEVICE_FF);
  }

  private static getFromSession(): String | undefined {
    return sessionStorage.getItem(CHECKOUT_SESSION_STORAGE_RAVELIN_DEVICE_ID);
  }

  private static getFeatureFlagSession(): string | undefined {
    return sessionStorage.getItem(CHECKOUT_SESSION_STORAGE_RAVELIN_DEVICE_FF);
  }

  getRavelinObjectParam(): Partial<Record<keyof Pick<AdditionalInformation, 'ravelinDeviceId'>, string>> {
    const ravelinDeviceId = (this.route?.snapshot?.queryParams ?? {})[RavelinService.ROUTER_ID];
    if (!ravelinDeviceId) {
      return {};
    }
    return { ravelinDeviceId };
  }
}
